// @flow
// later
/* eslint-disable ta-code-conventions/has-matching-test, ta-code-conventions/has-matching-storydoc */
/* eslint-disable prefer-destructuring */

import * as React from 'react';
import { unescaped } from '@ta/common.i18n';

import type { NavMenu } from './types';
import { handleClick } from './handlers';
import styles from './DropDownMenus.less';

/** Props for DropDownMenus */
export type Props = {|
  /** the menus to show in the dropdown menu bar */
  menus: ?Array<NavMenu>,
  /** hidden links that the mobile hamburger menu will hackily clone and/or move away */
  mobileLocationMenus: ?Array<NavMenu>,
|};

type State = {
  // Menus in the menu bar
  barItems: Array<NavMenu>,
  // Menus under "..."
  moreItems: Array<NavMenu>,
  allItems: Array<NavMenu>,
};

const ITEM_HEIGHT = 34;

const getMenuHref = (menu: NavMenu) => {
  if (menu.action) {
    if (menu.action.href) {
      return menu.action.href;
    }
    if (menu.action.type === 'URL' && menu.action.data) {
      return menu.action.data;
    }
  }
  return `${'javascript'}${':void'}`;
};

const getMenuHandler = (menu: NavMenu) => {
  if (!menu.action || !menu.action.data) {
    return null; // default anchor action
  }
  const action = menu.action;
  return ev => handleClick(ev, action);
};

const getMenuLabel = (menu: NavMenu) => {
  if (menu.label === '...') {
    return <span className="ui_icon more-horizontal" />;
  }
  if (menu.icon) {
    return (
      <React.Fragment>
        <span className={`ui_icon ${styles.icon} ${menu.icon}`} />
        {unescaped(menu.label)}
      </React.Fragment>
    );
  }
  return unescaped(menu.label);
};

const helpCenterOnClick = (e: SyntheticEvent<*>) => {
  const target = e.target;
  global.require(
    ['trjs!brand/HelpCenterOverlay', 'trcss!src/build/required/help_center_overlay'],
    o => {
      o.open(target, '/uvpages/helpCenterOverlay.html');
    },
  );
};

const renderHelpCenterItem = (menu: NavMenu) => (
  <li className={styles.item} key={`${menu.label}.${menu.icon || ''}`}>
    <div className={styles.link} onClick={e => helpCenterOnClick(e)}>
      {getMenuLabel(menu)}
    </div>
  </li>
);

const renderURLItem = (menu: NavMenu) => (
  <li className={styles.item} key={`${menu.label}.${menu.icon || ''}`}>
    <a
      href={getMenuHref(menu)}
      className={`${styles.link} ${menu.items && menu.items.length > 0 ? styles.expandable : ''}`}
      onClick={getMenuHandler(menu)}
    >
      {getMenuLabel(menu)}
    </a>
    {menu.items && menu.items.length > 0 ? (
      <div className={styles.flyout}>
        {/* eslint-disable-next-line no-use-before-define */}
        <ul className={styles.flyoutlist}>{menu.items.map(renderItem)}</ul>
      </div>
    ) : null}
  </li>
);

const renderItem = (menu: NavMenu) => {
  if (menu.action && menu.action.type === 'HELP_CENTER') {
    return renderHelpCenterItem(menu);
  }
  return renderURLItem(menu);
};

const measureMaxHeight = (menu: NavMenu) => {
  let h = 1;
  if (menu.items && menu.items.length > 0) {
    if (menu.items.length > h) {
      h = menu.items.length;
    }
    h = menu.items.map(measureMaxHeight).reduce((x, y) => Math.max(x, y), h);
  }
  return h;
};

const renderMenu = (menu: NavMenu) => {
  const minh = measureMaxHeight(menu) * ITEM_HEIGHT;
  const listStyle = {
    minHeight: `${minh}px`,
  };
  return (
    <li
      className={`${styles.menu} ${menu.active ? styles.active : ''} ${
        menu.label === '...' ? styles.ellipsis : ''
      }`}
      key={`${menu.label}.${menu.icon || ''}`}
    >
      <a className={styles.menulink} href={getMenuHref(menu)} onClick={getMenuHandler(menu)}>
        {getMenuLabel(menu)}
      </a>
      {menu.items && menu.items.length > 0 ? (
        <div className={styles.dropdown}>
          <ul className={styles.dropdownlist} style={listStyle}>
            {menu.items.map(renderItem)}
          </ul>
        </div>
      ) : null}
    </li>
  );
};

const renderHiddenMobile = (menu: NavMenu) => {
  let label = null;
  if (menu.icon) {
    label = (
      <React.Fragment>
        <span className={`ui_icon ${styles.icon} ${menu.icon}`} />
        {unescaped(menu.label)}
      </React.Fragment>
    );
  } else {
    label = unescaped(menu.label);
  }

  const cls = menu.isMore ? 'nav-sub-item force-more' : 'nav-sub-item';

  return (
    <li className={cls} key={`${menu.label}.${menu.icon || ''}`}>
      <a className="global-nav-link" href={getMenuHref(menu)} onClick={getMenuHandler(menu)}>
        {label}
      </a>
    </li>
  );
};

class DropDownMenus extends React.Component<Props, State> {
  static getDerivedStateFromProps(props: Props, state: State) {
    if ((props.menus || []).length !== state.allItems.length) {
      return ({
        barItems: (props.menus || []).filter(m => !m.isMore),
        moreItems: (props.menus || []).filter(m => !!m.isMore),
        allItems: props.menus || [],
      }: State);
    }
    return {};
  }

  constructor(props: Props) {
    super(props);

    this.state = {
      barItems: (props.menus || []).filter(m => !m.isMore),
      moreItems: (props.menus || []).filter(m => !!m.isMore),
      allItems: props.menus || [],
    };
    this.barRef = React.createRef();
  }

  // find out how many menu bar items are wrapped, and add those to the more menu

  setMore() {
    const { barItems } = this.state;
    const menus = this.barRef.current && this.barRef.current.children;
    if (!menus || menus.length !== barItems.length) {
      return;
    }
    let wrapped = 0;
    for (let i = 0; i < menus.length; ++i) {
      const menu = menus[i];
      if (menu.offsetTop && menu.offsetTop > ITEM_HEIGHT / 2) {
        ++wrapped;
      }
    }
    const moreItems = this.state.moreItems.filter(m => !!m.isMore);
    for (let i = 1; i <= wrapped; ++i) {
      moreItems.unshift(barItems[barItems.length - i]);
    }
    if (moreItems.length !== this.state.moreItems.length) {
      this.setState({ moreItems });
    }
  }

  barRef: any;

  render() {
    return (
      <div className={`is-hidden-mobile ${styles.container}`}>
        <div className={styles.inner}>
          <ul className={styles.bar} ref={this.barRef}>
            {this.state.barItems.map(renderMenu)}
          </ul>
          <ul className={styles.bar} onMouseEnter={() => this.setMore()}>
            {renderMenu({ label: '...', items: this.state.moreItems })}
          </ul>
        </div>
        {!this.props.mobileLocationMenus ? null : (
          <div className={`${styles.mobHidden} global-nav-links-container`}>
            <ul className="global-nav-links-menu">
              {(this.props.mobileLocationMenus || []).map(renderHiddenMobile)}
            </ul>
          </div>
        )}
      </div>
    );
  }
}

export default DropDownMenus;
